import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Upload, X, Camera, Loader2, AlertCircle } from "lucide-react";
import React, { useState, useRef } from "react";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ImageUploader = ({
  id,
  cloudName,
  maxFileSize = MAX_FILE_SIZE,
  maxImages = 5,
  onError,
  onImageUpload,
  tabIndex,
  uploadPreset,
}) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Validate required props
  if (!cloudName || !uploadPreset) {
    console.error(
      "ImageUploader: cloudName and uploadPreset are required props",
    );
  }

  const showError = (message) => {
    setError(message);
    onError?.(message);
    setTimeout(() => setError(null), 5000);
  };

  const handleFiles = async (files) => {
    if (images.length + files.length > maxImages) {
      showError(`You can only upload up to ${maxImages} images`);
      return;
    }

    setUploading(true);
    setError(null);
    const newImages = [];

    for (let file of files) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        showError(`${file.name} is not an image file`);
        continue;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        const sizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);
        showError(`${file.name} is too large. Maximum size is ${sizeMB}MB`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", uploadPreset);

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error?.message || "Upload failed");
        }

        const data = await response.json();

        const imageData = {
          id: data.public_id,
          url: data.secure_url.replace("/upload/", "/upload/f_auto,q_auto/"),
          thumbnail: data.secure_url.replace(
            "/upload/",
            "/upload/w_400,h_400,c_fill,ar_1:1,g_auto,q_auto,f_auto/",
          ),
          originalName: file.name,
        };

        newImages.push(imageData);
      } catch (error) {
        showError(`Failed to upload ${file.name}: ${error.message}`);
      }
    }

    const updatedImages = [...images, ...newImages];
    setImages(updatedImages);
    setUploading(false);

    if (onImageUpload) {
      onImageUpload(updatedImages);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Fix: Stop event bubbling
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Fix: Stop event bubbling
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Fix: Stop event bubbling
    setDragActive(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Fix: Only process if files were selected
      handleFiles(files);
    }
    e.target.value = "";
  };

  const removeImage = (indexToRemove) => {
    const updatedImages = images.filter((_, index) => index !== indexToRemove);
    setImages(updatedImages);

    if (onImageUpload) {
      onImageUpload(updatedImages);
    }
  };

  const openFileSelector = (e) => {
    e.preventDefault(); // Fix: Prevent accordion toggle
    e.stopPropagation(); // Fix: Stop event bubbling
    fileInputRef.current?.click();
  };

  return (
    <div id={id} className="w-100 p-1" tabIndex={tabIndex}>
      {" "}
      {/* Fix: Scrollable container */}
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}
      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div>
          <h4 className="font-sm mb-3">
            Uploaded Images ({images.length}/{maxImages})
          </h4>
          <div className="Row">
            {images.map((image, index) => (
              <div
                key={image.id}
                className="w-25 m-4 relative group Column Justify-Center"
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={image.thumbnail}
                    alt={image.originalName}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                </div>

                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeImage(index);
                  }}
                  className="absolute -top-2 -right-2  p-1 Delete-Icon"
                  aria-label="Remove image"
                  type="button"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        disabled={images.length >= maxImages}
      />
      {/* Drag and Drop Area  */}
      <div
        className={`
        upload-area p-4 text-center transition-all mb-3
          ${dragActive ? "border-blue-400 bg-blue-50" : "border-gray-300"}
          ${
            uploading || images.length >= maxImages
              ? "opacity-50"
              : "cursor-pointer"
          }
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={
          images.length < maxImages && !uploading ? openFileSelector : undefined
        }
      >
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-2" />
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : (
          <div>
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              Or drag and drop images here
            </p>
            <p className="text-xs text-gray-500 mt-1">
              JPG, PNG, GIF (max {(maxFileSize / (1024 * 1024)).toFixed(0)}MB
              each)
            </p>
          </div>
        )}
      </div>
      <button
        className="Green p-1 bord-15 Background-LGreen w-100 mb-3"
        onClick={openFileSelector}
        disabled={uploading || images.length >= maxImages}
        type="button"
      >
        {uploading
          ? "Uploading..."
          : `Upload Images (${images.length}/${maxImages})`}
      </button>
      {/* Max images warning */}
      {images.length === maxImages && (
        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <Camera className="h-4 w-4 text-yellow-600 mr-2" />
            <p className="text-sm text-yellow-800">
              Maximum images reached ({maxImages})
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
